// packages/client/src/modules/admin/components/DescontosClientesTab.tsx
// AIDEV-NOTE: Aba de desconto especial por cliente (politicas_cliente).
// Busca cliente via /clientes/busca, define % e lista/remoe os configurados.
import { useState } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import { Delete as DeleteIcon, Save as SaveIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/services/auth.service';

interface ClienteBusca {
  CODIGO: number;
  RAZAO_SOCIAL: string | null;
  CNPJ_CPF: string | null;
  CIDADE: string;
}

interface PoliticaCliente {
  cliente_erp_id: number;
  limite_desconto_pct: number;
  nome_razao_social: string | null;
  cpf_cnpj: string | null;
  cidade: string | null;
}

export function DescontosClientesTab() {
  const queryClient = useQueryClient();
  const [inputValue, setInputValue] = useState('');
  const [clienteSel, setClienteSel] = useState<ClienteBusca | null>(null);
  const [percentual, setPercentual] = useState('');
  const [erro, setErro] = useState('');

  const searchEnabled = inputValue.trim().length >= 3;

  const { data: clientes = [], isLoading: buscando } = useQuery({
    queryKey: ['admin-clientes-busca', inputValue.trim()],
    enabled: searchEnabled,
    staleTime: 30_000,
    queryFn: async (): Promise<ClienteBusca[]> => {
      const res = await api.get<{ clientes: ClienteBusca[] }>('/clientes/busca', {
        params: { q: inputValue.trim() }
      });
      return res.data.clientes;
    }
  });

  const politicasQuery = useQuery({
    queryKey: ['admin-politicas-clientes'],
    queryFn: async (): Promise<PoliticaCliente[]> => {
      const res = await api.get<{ politicas: PoliticaCliente[] }>('/admin/politicas-clientes');
      return res.data.politicas;
    }
  });

  const saveMutation = useMutation({
    mutationFn: async ({ clienteErpId, pct }: { clienteErpId: number; pct: number }) => {
      await api.put(`/admin/politicas-clientes/${clienteErpId}`, { limite_desconto_pct: pct });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-politicas-clientes'] });
      setClienteSel(null);
      setInputValue('');
      setPercentual('');
      setErro('');
    },
    onError: () => setErro('Erro ao salvar o desconto especial.')
  });

  const deleteMutation = useMutation({
    mutationFn: async (clienteErpId: number) => {
      await api.delete(`/admin/politicas-clientes/${clienteErpId}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-politicas-clientes'] })
  });

  const handleSalvar = () => {
    const pct = Number(percentual);
    if (!clienteSel || clienteSel.CODIGO <= 0) {
      setErro('Selecione um cliente sincronizado com o ERP.');
      return;
    }
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      setErro('Percentual deve estar entre 0 e 100.');
      return;
    }
    saveMutation.mutate({ clienteErpId: clienteSel.CODIGO, pct });
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Desconto especial por cliente
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        O limite abaixo substitui a política do vendedor (desconto de item e total) e permite
        desconto até o percentual configurado mesmo para vendedores sem permissão de desconto.
      </Typography>

      {erro && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Autocomplete
          sx={{ minWidth: 320, flex: 1 }}
          options={clientes}
          value={clienteSel}
          getOptionLabel={(o) => `${o.RAZAO_SOCIAL ?? ''} - ${o.CNPJ_CPF ?? ''} (${o.CIDADE})`}
          isOptionEqualToValue={(o, v) => o.CODIGO === v.CODIGO}
          loading={buscando}
          inputValue={inputValue}
          // AIDEV-NOTE: sem guard de reason — o 'reset' escreve o label do cliente
          // selecionado no input (feedback visual da seleção)
          onInputChange={(_, v) => setInputValue(v)}
          onChange={(_, v) => setClienteSel(v)}
          noOptionsText={
            searchEnabled ? 'Nenhum cliente encontrado' : 'Digite ao menos 3 caracteres'
          }
          renderInput={(params) => <TextField {...params} label="Buscar cliente" size="small" />}
        />
        <TextField
          label="Limite de desconto (%)"
          size="small"
          type="number"
          value={percentual}
          onChange={(e) => setPercentual(e.target.value)}
          inputProps={{ min: 0, max: 100, step: 0.5 }}
          sx={{ width: 180 }}
        />
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSalvar}
          disabled={saveMutation.isPending || !clienteSel || percentual === ''}
        >
          {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
        </Button>
      </Box>

      {politicasQuery.isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cliente</TableCell>
              <TableCell>CPF/CNPJ</TableCell>
              <TableCell>Cidade</TableCell>
              <TableCell align="right">Limite (%)</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(politicasQuery.data ?? []).map((p) => (
              <TableRow key={p.cliente_erp_id}>
                <TableCell>{p.nome_razao_social ?? `Código ${p.cliente_erp_id}`}</TableCell>
                <TableCell>{p.cpf_cnpj ?? '-'}</TableCell>
                <TableCell>{p.cidade ?? '-'}</TableCell>
                <TableCell align="right">{p.limite_desconto_pct}%</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => deleteMutation.mutate(p.cliente_erp_id)}
                    disabled={deleteMutation.isPending}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {(politicasQuery.data ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Nenhum cliente com desconto especial configurado.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
